import React, { useMemo, useState } from 'react';
import { Typography, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import List from '../../components/Templates/List';
import type { FieldConfig, ForgeQuery } from '../../components/ForgeQueryBuilder';
import { overdueApi } from '../../services/statistics/overdue';
import type { RepaymentLedgerJoined, InstallmentOrderJoinedListRequest } from '../../services/statistics/overdue';
import { useAuditors, useShopMap } from '../../services/store/organization.store';
import type { OverdueBillDashboardSummary } from '../../types/entity/overdue';
import { getDefaultQuery, getBillKpiStatic } from './effect';
import dayjs from 'dayjs';
import { EnvironmentOutlined, PhoneOutlined, UserOutlined } from '@ant-design/icons';

const { Text } = Typography;

const OverdueBillsStatisticsPage: React.FC = () => {
  const { shopList, shopMap } = useShopMap();
  const { auditorList } = useAuditors();

  const queryFields: FieldConfig[] = useMemo(() => [
    { key: 'rentalStartDate', group: 'installments', label: '账单日期', type: 'date', operators: ['between'], format: 'YYYY-MM-DD', placeholder: ['开店日期', '至今'] },
    {
      key: 'storeId', group: 'installments', label: '店铺', type: 'select', operators: ['eq'],
      options: shopList.map(shop => ({ label: `${shop.name}`, value: Number(shop.code) }))
    },
    { key: 'orderNo', group: 'installments', label: '订单ID', type: 'string', operators: ['like'] },
    { key: 'rentBillId', group: 'installments', label: '账单ID', type: 'string', operators: ['like'] },
    // { key: 'auditorId', group: 'order', label: '审核人', type: 'select', getOptions: () => [
    //   ...auditorList.map(a => ({ label: a.name, value: a.key }))
    // ] },
    {
      key: 'periodNumber', group: 'installments', label: '当期期数', type: 'select', operators: ['eq'],
      options: Array.from({ length: 23 }, (_, i) => ({ label: `M${i + 1}`, value: i + 2 }))
    },
    { key: 'overdueDays', group: 'installments', label: '逾期天数', type: 'number', operators: ['eq'] },
    {
      key: 'appType', group: 'installments', label: '订单渠道', type: 'select', operators: ['in'],
      options: [
        { label: '人人租app', value: '人人租app' },
        { label: '用户端APP', value: '用户端APP' },
        { label: '个人版小程序', value: '个人版小程序' },
        { label: '支付宝小程序', value: '支付宝小程序' },
        { label: '微信小程序', value: '微信小程序' },
      ]
    },
    {
      key: 'riskQualitative', group: 'order', label: '风控类型', type: 'select', operators: ['eq'],
      options: [
        { label: '盲发', value: '盲发', queryValue: { key: 'isBlindShip', operator: 'eq', value: true } },
        { label: '通过', value: '通过', queryValue: { key: 'riskQualitative', operator: 'eq', value: '通过' } },
        { label: '预收', value: '预收', queryValue: { key: 'riskQualitative', operator: 'eq', value: '预收' } },
        { label: '高风险', value: '不通过', queryValue: { key: 'riskQualitative', operator: 'eq', value: '不通过' } }
      ]
    },
    { key: 'lockDevice', group: 'order', label: '是否带锁', type: 'boolean', operators: ['eq'] },
  ], [shopList]);

  const columns: ColumnsType<RepaymentLedgerJoined> = [
    {
      title: '账单ID', key: 'rentBillId', width: 150,
      render: (_: any, rec) => <Text copyable={{ text: rec.installmentDetail.rentBillId }} style={{ fontSize: 12 }}>{rec.installmentDetail.rentBillId}</Text>
    },
    {
      title: '店铺', key: 'storeId', width: 120, fixed: 'left',
      render: (_: any, rec) => shopMap.get?.(Number(rec.installmentDetail.storeId))?.name || '-'
    },
    {
      title: '订单ID', key: 'orderNo', width: 150,
      render: (_: any, rec) => <Text copyable={{ text: rec.installmentDetail.orderNo }} style={{ fontSize: 12 }}>{rec.installmentDetail.orderNo}</Text>
    },
    {
      title: '账单租期',
      key: 'rentalPeriod',
      width: 180,
      render: (_: any, rec) => {
        const startDate = rec.installmentDetail.rentalStartDate ? dayjs(rec.installmentDetail.rentalStartDate) : null;
        const endDate = rec.installmentDetail.rentalEndDate ? dayjs(rec.installmentDetail.rentalEndDate) : null;
        if (startDate && endDate) {
          return (
            <Text style={{ fontSize: '12px' }}>
              {startDate.format('YYYY-MM-DD')} 至 {endDate.format('YYYY-MM-DD')}
            </Text>
          );
        }
        return <Text style={{ fontSize: '12px' }}>-</Text>;
      },
    },
    {
      title: '订单渠道', key: 'AppType', width: 120,
      render: (_: any, rec) => rec.installmentDetail.appType || '-'
    },
    {
      title: '当期期数', key: 'periodNumber', width: 90,
      render: (_: any, rec) => <Tag color="blue">M{rec.installmentDetail.periodNumber - 1}</Tag>
    },
    {
      title: '逾期天数', key: 'overdueDays', width: 100,
      render: (_: any, rec) => {
        const expectedDate = rec.installmentDetail.expectedDeductionDate ? dayjs(rec.installmentDetail.expectedDeductionDate) : null;
        const now = dayjs();
        
        const isOverdue = rec.installmentDetail.isPeriodOverduing;
        const overdueDays = (isOverdue && expectedDate) ? now.diff(expectedDate, 'day') : 0;

        return <Tag color={isOverdue ? (overdueDays > 30 ? 'red' : overdueDays > 7 ? 'orange' : 'yellow') : 'green'}>
          {isOverdue ? `${overdueDays}天` : '未逾期'}
        </Tag>;
      }
    },

    {
      title: '设备带锁', key: 'lockDevice', width: 100,
      render: (_: any, rec) => {
        const v = rec.orderData?.lockDevice || false;
        return <Tag color={v ? 'green' : 'orange'}>{v ? '带锁' : '不带锁'}</Tag>;
      }
    },
    // 逾期总金额
    {
      title: '逾期总金额', key: 'overdueTotalAmount', width: 120,
      render: (_: any, rec) => {
        const amount = parseFloat(rec.installmentDetail.billAmount || '0');
        return <Text>¥{amount.toFixed(2)}</Text>;
      }
    },
    // 坏账金额
    {
      title: '预计坏账金额', key: 'badDebtAmount', width: 120,
      render: (_: any, rec) => {
        return <Text>¥{rec.orderData?.expectedRecoveryAmount || 0}</Text>;
      }
    },
    // 原押金
    {
      title: '冻结押金', key: 'depositValue', width: 120,
      render: (_: any, rec) => {
        return <Text>¥{rec.orderData?.depositValue || 0}</Text>;
      }
    },
    // 商品价值
    {
      title: '商品价值', key: 'productValue', width: 120,
      render: (_: any, rec) => {
        return <Text>¥{rec.orderData?.productValue || 0}</Text>;
      }
    },
    // 用户信息
        {
      title: '用户信息',
      key: 'userInfo',
      width: 250,
      render: (_: any, record: RepaymentLedgerJoined) => (
        <div style={{ fontSize: '12px' }}>
          <div>
            <UserOutlined style={{ marginRight: 4 }} />
            <Text>{record.orderData?.userAuthName || '-'}</Text>
          </div>
          <div>
            <PhoneOutlined style={{ marginRight: 4 }} />
            <Text copyable={{ text: record.orderData?.userPhone || '-' }}>{record.orderData?.userPhone || '-'}</Text>
          </div>
          <Tooltip title={`${record.orderData?.province || '-'} ${record.orderData?.city || '-'} ${record.orderData?.district || '-'}`}>
            <div style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <EnvironmentOutlined style={{ marginRight: 4 }} />
              <Text>{record.orderData?.userAddress || '-'}</Text>
            </div>
          </Tooltip>
        </div>
      ),
    },
    // 风控类型
    {
      title: '风控类型', key: 'riskQualitative', width: 120,
      render: (_: any, rec) => {
        let riskQualitative = rec.orderData?.riskQualitative  || '未知';
        if (rec.orderData?.isBlindShip) riskQualitative = '盲发';
        // 盲发绿色， 通过蓝色， 预收橙色， 不通过红色
        let showColor
        switch (riskQualitative) {
          case '通过':
            showColor = 'blue';
            break;
          case '盲发':
            showColor = 'green';
            break;
          case '预收':
            showColor = 'orange';
            break;
          case '不通过':
            showColor = 'red';
            break;
          default:
            showColor = 'yellow';
            break;
        }
        return <Tag color={showColor}>{riskQualitative}</Tag>
      }
    },
    // 审核人
    // {
    //   title: '审核人', key: 'approverName', width: 120,
    //   render: (_: any, rec) => rec.orderData?.approverName || '-'
    // },
  ];

  return (
    <List<RepaymentLedgerJoined, InstallmentOrderJoinedListRequest, OverdueBillDashboardSummary>
      title={"逾期账单数据总览"}
      queryFields={queryFields}
      kpiItems={getBillKpiStatic}
      columns={columns}
      rowKey={(r) => String(r.installmentDetail?.rentBillId || r.installmentDetail?.orderNo)}
      fetcher={async (params) => {
        // Deep clone params to modify filter safely
        const newParams = JSON.parse(JSON.stringify(params));
        
        // 处理逾期天数查询，转换为应扣日期查询
        // params structure is { filter: { installments: {...}, order: {...} }, ... }
        if (newParams.filter?.installments?.overdueDays) {
          let daysVal = newParams.filter.installments.overdueDays;
          if (typeof daysVal === 'object' && daysVal !== null && 'value' in daysVal) {
            daysVal = daysVal.value;
          }
          const days = Number(daysVal);
          
          // 逾期 N 天，意味着应扣日期是 N 天前
          // 例如今天 10号，逾期 1 天，应扣日期是 9号
          const targetDate = dayjs().subtract(days, 'day').format('YYYY-MM-DD');
          newParams.filter.installments.expectedDeductionDate = { operator: 'eq', value: targetDate };
          newParams.filter.installments.isPeriodOverduing = { operator: 'eq', value: true };
          delete newParams.filter.installments.overdueDays;
        }

        const [joinedResp, stats] = await Promise.all([
          overdueApi.getInstallmentsJoinedList(newParams),
          overdueApi.getOverdueBillsSummary(newParams)
        ]);
        return { rows: joinedResp.data.items as any, stats: stats, pagination: joinedResp.data.pagination };
      }}
      // defaultQuery={getDefaultQuery({}, 'order')}
    />
  );
};

export default OverdueBillsStatisticsPage;
